import React, { forwardRef, useEffect, useMemo, useState } from 'react'
import uniqueId from 'lodash/uniqueId'
import { HiddenQuestionsController } from '@core/components/templateAnswersForm/HiddenQuestionsController'
import { Box, Flex, Text } from '@fundamentals'
import { useFormContext, useWatch } from 'react-hook-form'
import { localizedStrings } from '@core/strings'
import { IconButton } from '@common/material'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import { SmartToy } from '@mui/icons-material'
import { useModal } from '@common/GlobalModal'
import { AIPrefillWorkflowModal } from '@modules/ai/AIPrefillWorkflowModal'
import {
  VirtualizedQuestionGroupList,
  VirtualizedQuestionGroupListHandle,
} from '@elements/Questions/VirtualizedQuestionGroupList'

type QuestionsFormProps = {
  organisationId: number
  questionsFormKey: string
}

const QuestionsForm = forwardRef<
  VirtualizedQuestionGroupListHandle,
  QuestionsFormProps
>(({ organisationId, questionsFormKey }, ref) => {
  const { flags } = useFeatureFlags(['ai_prefill_workflow'])
  const { showModal } = useModal()
  const { watch, formState, setValue } = useFormContext()
  const questions = watch(questionsFormKey)
  // Remount the list when the questions are replaced (e.g. switching
  // template), so no row outlives the question it was built for. An exact
  // useWatch only changes on setValue of the array or reset(); watch() also
  // changes once the form mounts, which remounted the list mid-click.
  const replacedQuestions = useWatch({ name: questionsFormKey, exact: true })
  const questionsListKey = useMemo(
    () => uniqueId('questions-'),
    [replacedQuestions],
  )
  const [reRender, setRerender] = useState(false)
  useEffect(() => {
    if (reRender) {
      setRerender(false)
    }
  }, [reRender])

  //HACK ALERT: This is necessary for the formState to give the correct values
  formState.isSubmitted
  formState.isSubmitSuccessful
  formState.isValid

  return (
    <>
      {/*  HACK ALERT: had force a re-render so that the prefilled answers show up*/}
      {!reRender && (
        <>
          {flags.ai_prefill_workflow && (
            <Flex>
              <Box flexGrow={1} />
              <IconButton
                onClick={() => {
                  showModal(
                    <AIPrefillWorkflowModal
                      questions={questions}
                      onSuccess={(value) => {
                        setValue(questionsFormKey, value)
                        setRerender(true)
                      }}
                    />,
                  )
                }}
              >
                <SmartToy />
              </IconButton>
            </Flex>
          )}
          {formState.isSubmitted &&
            formState.isSubmitSuccessful === false &&
            !formState.isValid && (
              <Text color='error' mb={2}>
                {localizedStrings.pleaseCompleteAllQuestions}
              </Text>
            )}
          <VirtualizedQuestionGroupList
            key={questionsListKey}
            ref={ref}
            questionGroups={questions ?? []}
            organisationId={organisationId}
            questionsFormKey={questionsFormKey}
          />
          <HiddenQuestionsController />
        </>
      )}
    </>
  )
})

QuestionsForm.displayName = 'QuestionsForm'

export { QuestionsForm }
